import React, { useState, useEffect, useCallback, useRef } from "react";
import Icon from "../AppIcon";
import { useLocationContext } from "../../contexts/LocationContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";
import { integrationsApi } from "../../services/api/integrations";

const NavigationHeader = ({ isCollapsed, onToggleSidebar }) => {
  const { currentLocation, locations, setCurrentLocation } = useLocationContext();
  const { selectedTenant, currentTenant, tenants, setSelectedTenant, hasMultipleTenants } = useTenantContext();
  const { isGlobalAdmin, user } = useAuth();

  const [syncStatus, setSyncStatus] = useState({
    isConnected: false,
    lastSync: null,
    isSyncing: false,
    hrProvider: null,
    posProvider: null,
  });
  const [syncError, setSyncError] = useState(null);
  const [contextOpen, setContextOpen] = useState(false);
  const contextRef = useRef(null);

  // Get the active tenant
  const activeTenant = isGlobalAdmin ? selectedTenant : currentTenant;

  // Show location selector if user has locations
  const isRegularEmployee = user?.role === "User";
  const showLocationSelector = !isRegularEmployee && locations && locations.length > 0;
  const showTenantSelector = isGlobalAdmin && tenants && tenants.length > 0;

  // Close context panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextRef.current && !contextRef.current.contains(event.target)) {
        setContextOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch integration status on mount and when tenant changes
  const fetchIntegrationStatus = useCallback(async () => {
    if (!activeTenant?.id) return;

    try {
      const response = await integrationsApi.getStatus(activeTenant.id);
      if (response.success && response.data) {
        setSyncStatus((prev) => ({
          ...prev,
          isConnected: response.data.hasHrIntegration || response.data.hasPosIntegration,
          hrProvider: response.data.hrProvider,
          posProvider: response.data.posProvider,
          lastSync: response.data.lastTillSync || response.data.lastHrSync
            ? new Date(response.data.lastTillSync || response.data.lastHrSync)
            : null,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch integration status:", err);
    }
  }, [activeTenant?.id]);

  useEffect(() => {
    fetchIntegrationStatus();

    // Refresh status every 60 seconds
    const interval = setInterval(fetchIntegrationStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchIntegrationStatus]);

  const formatLastSync = (date) => {
    if (!date) return "Never";
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const handleManualSync = async () => {
    if (!activeTenant?.id || syncStatus.isSyncing) return;

    setSyncStatus((prev) => ({ ...prev, isSyncing: true }));
    setSyncError(null);

    try {
      const response = await integrationsApi.quickSync(activeTenant.id);

      if (response.success) {
        const data = response.data;
        setSyncStatus((prev) => ({
          ...prev,
          isSyncing: false,
          lastSync: new Date(data.syncedAt || Date.now()),
        }));

        // Show success notification (you might want to use a toast library)
        console.log(
          `Sync completed: ${data.clockRecordsCreated + data.clockRecordsUpdated} clock records, ` +
          `${data.troncRecordsCreated + data.troncRecordsUpdated} TRONC records`
        );
      } else {
        throw new Error(response.message || "Sync failed");
      }
    } catch (err) {
      console.error("Sync failed:", err);
      setSyncError(err.message);
      setSyncStatus((prev) => ({ ...prev, isSyncing: false }));
    }
  };

  const headerLeftPosition = isCollapsed ? "lg:left-16" : "lg:left-64";

  // Build the status bar text: "Tenant Name - Location Name"
  const getStatusText = () => {
    const parts = [];

    if (activeTenant) {
      parts.push(activeTenant.name);
    }

    if (currentLocation) {
      parts.push(currentLocation.name);
    }

    return parts.length > 0 ? parts.join(" - ") : "No Location Selected";
  };

  // Get connection status text
  const getConnectionText = () => {
    if (syncStatus.isSyncing) return "Syncing...";
    if (!syncStatus.isConnected) return "No Integration";
    if (syncStatus.posProvider) return `${syncStatus.posProvider} Connected`;
    if (syncStatus.hrProvider) return `${syncStatus.hrProvider} Connected`;
    return "Connected";
  };

  return (
    <header
      className={`fixed top-0 right-0 left-0 ${headerLeftPosition} z-[999] bg-card border-b border-border transition-all duration-300`}
    >
      <div className="flex items-center justify-between px-4 py-2">
        {/* Left Side - Mobile Menu Button + Tenant/Location Status */}
        <div className="flex items-center gap-3">
          {/* Mobile Menu Toggle Button */}
          <button
            onClick={onToggleSidebar}
            className="lg:hidden p-2 hover:bg-muted rounded-md transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name="Menu" size={20} />
          </button>

          {/* Tenant and Location Context - Clickable with popout */}
          <div ref={contextRef} className="relative">
            <button
              onClick={() => setContextOpen(!contextOpen)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
              title="Change tenant or location"
            >
              <Icon name="Building2" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">
                {getStatusText()}
              </span>
              {(showTenantSelector || showLocationSelector) && (
                <Icon name="ChevronDown" size={14} className={`text-muted-foreground transition-transform ${contextOpen ? "rotate-180" : ""}`} />
              )}
            </button>

            {/* Context Pop-out Panel */}
            {contextOpen && (showTenantSelector || showLocationSelector) && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                {/* Tenant Selector */}
                {showTenantSelector && (
                  <div className="p-3 border-b border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tenant</p>
                    <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                      {selectedTenant && (
                        <button
                          onClick={() => { setSelectedTenant(null); setContextOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg hover:bg-muted transition-colors text-sm"
                        >
                          <Icon name="X" size={14} className="text-muted-foreground" />
                          <span>Clear Selection</span>
                        </button>
                      )}
                      {tenants.map((tenant) => (
                        <button
                          key={tenant.id}
                          onClick={() => { setSelectedTenant(tenant); setContextOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg transition-colors text-sm ${
                            selectedTenant?.id === tenant.id ? "bg-primary/10 text-primary" : "hover:bg-muted"
                          }`}
                        >
                          <Icon
                            name={selectedTenant?.id === tenant.id ? "CheckCircle" : "Circle"}
                            size={14}
                            className={selectedTenant?.id === tenant.id ? "text-primary" : "text-muted-foreground"}
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium block truncate">{tenant.name}</span>
                            <span className="text-xs text-muted-foreground">{tenant.slug}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location Selector */}
                {showLocationSelector && locations.length > 0 && (
                  <div className="p-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Location</p>
                    <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                      {locations.length > 1 && (
                        <button
                          onClick={() => { setCurrentLocation(null); setContextOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg transition-colors text-sm ${
                            !currentLocation ? "bg-primary/10 text-primary" : "hover:bg-muted"
                          }`}
                        >
                          <Icon name={!currentLocation ? "CheckCircle" : "Circle"} size={14}
                            className={!currentLocation ? "text-primary" : "text-muted-foreground"} />
                          <span>All Locations</span>
                        </button>
                      )}
                      {locations.map((loc) => (
                        <button
                          key={loc.id}
                          onClick={() => { setCurrentLocation(loc); setContextOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg transition-colors text-sm ${
                            currentLocation?.id === loc.id ? "bg-primary/10 text-primary" : "hover:bg-muted"
                          }`}
                        >
                          <Icon
                            name={currentLocation?.id === loc.id ? "CheckCircle" : "MapPin"}
                            size={14}
                            className={currentLocation?.id === loc.id ? "text-primary" : "text-muted-foreground"}
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium block truncate">{loc.name}</span>
                            {loc.code && <span className="text-xs text-muted-foreground">{loc.code}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Till/POS System Status */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50">
            <div
              className={`w-2 h-2 rounded-full ${syncStatus.isConnected ? "bg-green-500" : "bg-yellow-500"} ${syncStatus.isSyncing ? "animate-pulse" : ""}`}
            />
            <span className="text-sm text-muted-foreground">
              {getConnectionText()}
            </span>
            {syncStatus.isConnected && !syncStatus.isSyncing && syncStatus.lastSync && (
              <span className="text-xs text-muted-foreground/70 hidden sm:inline">
                • {formatLastSync(syncStatus.lastSync)}
              </span>
            )}
          </div>

          {/* Manual Sync Button */}
          <button
            onClick={handleManualSync}
            disabled={syncStatus.isSyncing || !syncStatus.isConnected}
            className="p-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !syncStatus.isConnected
                ? "Configure integrations first"
                : syncStatus.isSyncing
                  ? "Syncing..."
                  : "Sync clock records and TRONC"
            }
          >
            <Icon
              name="RefreshCw"
              size={18}
              className={syncStatus.isSyncing ? "animate-spin" : ""}
            />
          </button>

          {/* Error indicator */}
          {syncError && (
            <div className="hidden sm:flex items-center gap-1 text-xs text-error">
              <Icon name="AlertCircle" size={14} />
              <span>Sync failed</span>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default NavigationHeader;
